import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import moment from 'moment';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';

import { popupData, PopupNames } from 'core';
import { usePopupStateOperations, useInvoiceMatching } from 'core/hooks';
import {
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
  StudentAgreementsForLinkingDocument,
  StudentAgreementsForLinkingQuery,
  StudentAgreementsForLinkingQueryVariables,
} from 'core/graphql';
import { Button } from 'atoms';

import { PopupContainer } from '../PopupContainer';

/**
 * Invoice-linkage popup. ТЗ 0004: the single flow — pick a student →
 * agreement → unpaid ВЗНОС, then `linkInvoiceToContribution` pins the
 * invoice to it. No Payment is created here: the backend provisions the
 * hidden one when the invoice is paid. The legacy payment-link and
 * create-Payment modes were removed from the UI (PO decision 2026-07-03).
 *
 * Replaces the legacy `MatchInvoicePopup` for any new code path. The old
 * popup stays mounted (`PopupNames.matchInvoice`) for any rare caller that
 * still wants the deprecated `matchFakturowniaInvoice` mutation.
 */
export interface ILinkInvoicePaymentPopupData {
  invoiceId: string;
  /** Buyer email from the invoice — prefilled as the student search query. */
  buyerEmail?: string | null;
  /** When the auto-matcher already guessed a student via email, preselect it. */
  prefillStudentId?: string | null;
  /** Run after a successful link — the host page should refetch. */
  onLinked?: () => void;
}

interface ISelectOption {
  value: string;
  label: string;
}

interface IAgreementOption extends ISelectOption {
  currency?: string | null;
  groupName?: string | null;
  terminated?: boolean;
}

interface IContributionOption extends ISelectOption {
  agreementId: string;
}

/** Spec 0004: `status` is a plain lowercase String; a взнос is linkable
 *  while it isn't fully paid. Falls back to `complete` on old backends. */
const isUnpaidContribution = (node: {
  status?: string | null;
  complete?: boolean | null;
}): boolean => {
  const s = (node.status || '').toLowerCase();
  if (s) return s !== 'paid' && s !== 'paid_manual';
  return !node.complete;
};

export const LinkInvoicePaymentPopup = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const data = popupData()[PopupNames.linkInvoicePayment];

  const apollo = useApolloClient();
  const { linkToContribution, loading } = useInvoiceMatching();

  const [student, setStudent] = useState<ISelectOption | null>(null);
  const [agreement, setAgreement] = useState<IAgreementOption | null>(null);
  const [contribution, setContribution] = useState<IContributionOption | null>(
    null,
  );

  const [agreementOpts, setAgreementOpts] = useState<IAgreementOption[]>([]);
  const [contributionOpts, setContributionOpts] = useState<
    IContributionOption[]
  >([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [agreementsError, setAgreementsError] = useState<string | null>(null);

  const close = useCallback(() => {
    setStudent(null);
    setAgreement(null);
    setContribution(null);
    setAgreementOpts([]);
    setContributionOpts([]);
    setAgreementsError(null);
    hidePopup(PopupNames.linkInvoicePayment);
  }, [hidePopup]);

  // Pull the agreements + their contribution schedule of the chosen student.
  // Errors are caught explicitly so a backend hiccup doesn't bubble up as an
  // unhandled rejection — we render a soft notice instead.
  useEffect(() => {
    if (!student) {
      setAgreementOpts([]);
      setContributionOpts([]);
      setAgreement(null);
      setContribution(null);
      setAgreementsError(null);
      return;
    }
    let cancelled = false;
    setLoadingAgreements(true);
    setAgreementsError(null);
    apollo
      .query<
        StudentAgreementsForLinkingQuery,
        StudentAgreementsForLinkingQueryVariables
      >({
        query: StudentAgreementsForLinkingDocument,
        variables: { studentId: student.value },
        fetchPolicy: 'no-cache',
      })
      .then((result) => {
        if (cancelled) return;
        const agreements = result.data?.student?.agreements || [];
        const aOpts: IAgreementOption[] = [];
        const cOpts: IContributionOption[] = [];
        agreements.forEach((a) => {
          if (!a) return;
          // Terminated agreements stay in the list (marked) — an invoice
          // issued before the termination still links to their взносы.
          const groupName = a.group?.name || '';
          const baseLabel = groupName
            ? `${a.agreementTitle} · ${groupName}`
            : a.agreementTitle;
          aOpts.push({
            value: a.id,
            label: a.terminated
              ? `${baseLabel} · ${t(
                  'pages.fakturownia.linkPaymentPopup.terminatedSuffix',
                )}`
              : baseLabel,
            currency: a.currency,
            groupName,
            terminated: !!a.terminated,
          });
          // Spec 0004: unpaid взносы of the agreement — the link target.
          // Paid ones are omitted (backend rejects re-linking).
          (a.contributions?.edges || []).forEach((edge) => {
            const node = edge?.node;
            if (!node || !isUnpaidContribution(node)) return;
            const cDate = node.date
              ? moment(node.date).format('DD.MM.YYYY')
              : '—';
            const cAmount = `${node.amountForeign ?? node.amountByn ?? '—'} ${
              a.currency ?? ''
            }`.trim();
            const statusCode = (node.status || '').toLowerCase();
            const statusLabel = statusCode
              ? t(
                  `pages.studentDetail.payments.schedule.status.${statusCode}`,
                  {
                    defaultValue: statusCode,
                  },
                )
              : '';
            cOpts.push({
              value: node.id,
              agreementId: a.id,
              label: [`${cDate} · ${cAmount}`, statusLabel, a.agreementTitle]
                .filter(Boolean)
                .join(' · '),
            });
          });
        });
        setAgreementOpts(aOpts);
        setContributionOpts(cOpts);
        // Auto-pick when there's only one agreement — the most common case
        // and one fewer click for finance staff.
        if (aOpts.length === 1) setAgreement(aOpts[0]);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setAgreementOpts([]);
        setContributionOpts([]);
        setAgreementsError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingAgreements(false);
      });
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [student, apollo, t]);

  // When the agreement changes, narrow the взнос options to that one.
  const visibleContributionOpts = useMemo(
    () =>
      agreement
        ? contributionOpts.filter((c) => c.agreementId === agreement.value)
        : contributionOpts,
    [contributionOpts, agreement],
  );

  // Async student search — same shape MatchInvoicePopup uses, with the
  // buyer email auto-prefilled so the popular «student already exists with
  // this email» path is one Enter away.
  const loadStudentOptions = useCallback(
    async (input: string): Promise<ISelectOption[]> => {
      try {
        const result = await apollo.query<
          PaginatedStudentsQuery,
          PaginatedStudentsQueryVariables
        >({
          query: PaginatedStudentsDocument,
          variables: { query: input || data?.buyerEmail || '', first: 20 },
          fetchPolicy: 'no-cache',
        });
        const edges = result.data?.allStudents?.edges || [];
        const opts: ISelectOption[] = [];
        edges.forEach((edge) => {
          const node = edge?.node;
          if (!node || !node.user) return;
          const u = node.user;
          const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
          opts.push({
            value: node.id,
            label: `${fullName || '—'} · ${u.email || ''}`,
          });
        });
        return opts;
      } catch {
        return [];
      }
    },
    [apollo, data?.buyerEmail],
  );

  const canSubmit = useMemo(
    () => Boolean(data && student && agreement && contribution),
    [data, student, agreement, contribution],
  );

  const onSubmit = useCallback(async () => {
    if (!data || !agreement || !contribution) return;
    const ok = await linkToContribution(data.invoiceId, contribution.value);
    if (ok) {
      data.onLinked?.();
      close();
    }
  }, [data, agreement, contribution, linkToContribution, close]);

  if (!data) {
    return null;
  }

  return (
    <PopupContainer
      width="600px"
      onClose={close}
      title={t('pages.fakturownia.linkPaymentPopup.titleLinkContribution')}
    >
      <Body>
        {data.buyerEmail ? (
          <Hint>
            {t('pages.fakturownia.matchPopup.hint', { email: data.buyerEmail })}
          </Hint>
        ) : null}

        <Field>
          <Label>{t('pages.fakturownia.linkPaymentPopup.studentLabel')}</Label>
          <AsyncSelect
            autoFocus
            cacheOptions
            defaultOptions
            value={student}
            onChange={(v) => setStudent((v as ISelectOption) || null)}
            loadOptions={loadStudentOptions}
            placeholder={t('pages.fakturownia.matchPopup.studentPlaceholder')}
          />
        </Field>

        {student ? (
          <Field>
            <Label>
              {t('pages.fakturownia.linkPaymentPopup.agreementLabel')}
            </Label>
            <ReactSelect
              value={agreement}
              isLoading={loadingAgreements}
              options={agreementOpts}
              onChange={(v) => {
                setAgreement((v as IAgreementOption) || null);
                setContribution(null);
              }}
              // «нет активных договоров» ONLY when that is actually true —
              // as a blanket placeholder it read as a bug when agreements
              // were present but none selected yet.
              placeholder={
                agreementOpts.length || loadingAgreements
                  ? t('pages.fakturownia.linkPaymentPopup.agreementPlaceholder')
                  : t('pages.fakturownia.linkPaymentPopup.noActiveAgreements')
              }
            />
            {agreementsError ? (
              <ErrorNotice>{agreementsError}</ErrorNotice>
            ) : null}
          </Field>
        ) : null}

        {agreement ? (
          <Field>
            <Label>
              {t('pages.fakturownia.linkPaymentPopup.contributionLabel')}
            </Label>
            <ReactSelect
              value={contribution}
              options={visibleContributionOpts}
              onChange={(v) =>
                setContribution((v as IContributionOption) || null)
              }
              placeholder={t(
                'pages.fakturownia.linkPaymentPopup.contributionPlaceholder',
              )}
              noOptionsMessage={() =>
                t('pages.fakturownia.linkPaymentPopup.noUnpaidContributions')
              }
            />
            {visibleContributionOpts.length === 0 && !loadingAgreements ? (
              <EmptyContributions>
                {t('pages.fakturownia.linkPaymentPopup.noUnpaidContributions')}
              </EmptyContributions>
            ) : null}
          </Field>
        ) : null}

        <Actions>
          <Button
            onClick={close}
            variant="outline"
            colors={{
              backgroundColor: 'var(--k-bg-ffffff)',
              color: 'var(--k-fg-0d53fc)',
            }}
          >
            {t('pages.fakturownia.matchPopup.cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            variant="primary"
            disabled={!canSubmit || loading}
            loading={loading}
            colors={{
              backgroundColor: 'var(--k-bg-0d53fc)',
              color: 'var(--k-fg-ffffff)',
            }}
          >
            {t('pages.fakturownia.linkPaymentPopup.submitLinkContribution')}
          </Button>
        </Actions>
      </Body>
    </PopupContainer>
  );
});

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-475569);
`;

const Hint = styled.div`
  font-size: 12px;
  color: var(--k-fg-64748b);
  background: var(--k-bg-f8fafc);
  border: 1px solid var(--k-border-e2e8f0);
  padding: 8px 12px;
  border-radius: 8px;
`;

const ErrorNotice = styled.div`
  font-size: 12px;
  color: var(--k-fg-b91c1c);
  background: var(--k-bg-fef2f2);
  border: 1px solid var(--k-border-fecaca);
  padding: 8px 10px;
  border-radius: 6px;
`;

const EmptyContributions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: var(--k-fg-92400e);
  background: var(--k-bg-fffbeb);
  border: 1px solid var(--k-border-fde68a);
  padding: 8px 10px;
  border-radius: 6px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;
